/* eslint-disable react/jsx-no-duplicate-props */
/* eslint-disable no-unused-vars */
import { useState } from 'react';
import Sidebar from '../../components/Sidebar';
import Header from '../../components/Header';
import { openPrintPreview } from "../../components/Print";
import { Breadcrumb } from '../../components/Card/Cards';
import { Formik } from 'formik';
import { InputFromUser } from '../../components/Input/input';
import { Button, Row, Col, Table, Tag, Progress, message, Tooltip } from 'antd';
import {
  SendOutlined, CalendarOutlined, CheckCircleOutlined, PrinterOutlined
} from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { yogaFormValidationSchema } from '../../utils/FormValidation';
import PatientCCPaperView from '../../components/PatientCCPaperView/PatientCCPaperView';
import { ActionGetExerciseList } from '../../store/Actions/MasterDataAction';
import { safeArray, calculateAge } from '../../utils/functions';
import { ActionGetProgramSlotById, ActionUpdateProgramSlot, ActionGetTreatmentSlotById, ActionUpdateTreatmentSlot } from '../../store/Actions/TreatmentSlotAction';
import { useFetchOnce } from '../../hooks';
import './YogaConsultation.css';
import { useParams } from 'react-router-dom';
import { ActionGetServiceSlotById, ActionUpdateServiceSlot } from '../../store/Actions/ServiceSlotAction';
import { ActionGetSolutionSlotById, ActionUpdateSolutionSlot } from '../../store/Actions/SolutionSlotAction';

const BreadcrumbList = [
  {
    name: 'Yoga Consultation Form',
    linkTo: ``,
  },
];

const YogaConsultation = () => {
  const dispatch = useDispatch();
  const exercises = useSelector((state) => state.MasterData?.exercise);
  const [formData, setFormData] = useState(null);
  const [consultationDates, setConsultationDates] = useState([]);
  const { id, patientPlanId } = useParams();
  const url = new URL(window.location.href);
  const searchParams = url.searchParams;
  const treatmentSlotId = searchParams.get('treatmentSlotId');
  const solutionSlotId = searchParams.get('solutionSlotId');
  const serviceSlotId = searchParams.get('serviceSlotId');
  const programSlotId = searchParams.get('programSlotId');
  const roomName = searchParams.get('roomName');
  const roomType = searchParams.get('roomType');

  const slotAction = treatmentSlotId ? ActionGetTreatmentSlotById : serviceSlotId ? ActionGetServiceSlotById : programSlotId ? ActionGetProgramSlotById : ActionGetSolutionSlotById;
  const slotUpdateAction = treatmentSlotId ? ActionUpdateTreatmentSlot : serviceSlotId ? ActionUpdateServiceSlot : programSlotId ? ActionUpdateProgramSlot : ActionUpdateSolutionSlot;
  const slotId = treatmentSlotId ? treatmentSlotId : serviceSlotId ? serviceSlotId : programSlotId ? programSlotId : solutionSlotId;
  const patientDetail = useSelector(state => state?.patient?.CcPaperDetail);

  // Helper function to build print data
  const getPrintData = () => {
    return patientDetail || {
      name: 'Patient',
      registrationNo: '-'
    };
  };

  const handleSinglePrint = (values) => {
    const displayData = getPrintData();

    // 1. Patient Info Section
    const patientInfoSection = {
      title: 'Patient Information',
      fields: [
        { label: 'Name', value: displayData?.name || '-' },
        { label: 'Registration No', value: displayData?.registrationNo || '-' },
        { label: 'Age', value: displayData?.birthDate ? calculateAge(displayData.birthDate) : displayData?.age || '-' },
        { label: 'Gender', value: displayData?.gender || '-' },
        { label: 'Room Name', value: roomName || '-' },
        { label: 'Room Type', value: roomType || '-' },
      ]
    };

    // 2. Session Info (Complaints & Notes)
    const sessionInfoSection = {
      title: `Yoga Session: ${values.date ? moment(values.date).format('DD MMM YYYY') : '-'}`,
      fields: [
        { label: 'Session Date', value: values.date ? moment(values.date).format('DD MMMM YYYY (dddd)') : '-' },
        { label: 'Current Complaints', value: values.currentComplaints || '-' },
        { label: 'Notes', value: values.notes || '-' }
      ]
    };

    // 3. Exercise Table
    const exerciseData = values.exercise?.map((ex, index) => [
      index + 1,
      ex,
      values.exerciseCompletion?.[ex] ? '✓ Completed' : 'Pending'
    ]) || [];

    const completedCount = values.exercise?.filter(ex => values.exerciseCompletion?.[ex]).length || 0;
    const totalCount = values.exercise?.length || 0;

    const exerciseTable = {
      title: `Session Exercises (${completedCount}/${totalCount} Completed)`,
      type: 'table',
      headers: ['Sr.', 'Exercise Name', 'Status'],
      data: exerciseData
    };

    const sections = [patientInfoSection, sessionInfoSection];
    if (exerciseData.length > 0) {
      sections.push(exerciseTable);
    }

    openPrintPreview(displayData, 'Yoga Session Report', {
      sections,
      title: `Yoga Consultation: ${displayData?.name || 'Patient'}`
    });
  };

  const handleFullPrint = () => {
    const displayData = getPrintData();

    // 1. Patient Info Section
    const patientInfoSection = {
      title: 'Patient Information',
      fields: [
        { label: 'Name', value: displayData?.name || '-' },
        { label: 'Registration No', value: displayData?.registrationNo || '-' },
        { label: 'Age', value: displayData?.birthDate ? calculateAge(displayData.birthDate) : displayData?.age || '-' },
        { label: 'Gender', value: displayData?.gender || '-' },
        { label: 'Contact', value: displayData?.mobile || displayData?.phone || '-' },
        { label: 'Room Name', value: roomName || '-' },
        { label: 'Room Type', value: roomType || '-' },
      ]
    };

    // 2. Course Summary
    const sessionsWithData = consultationDates.filter(date => formData?.[date]?.[0]);
    const totalExercisesCompleted = sessionsWithData.reduce((acc, date) => {
      const dayData = formData?.[date]?.[0];
      return acc + (dayData?.exercise?.filter(e => e.completed === true || e.completed === 'true').length || 0);
    }, 0);
    const totalExercises = sessionsWithData.reduce((acc, date) => {
      const dayData = formData?.[date]?.[0];
      return acc + (dayData?.exercise?.length || 0);
    }, 0);

    // Find complaints for display (root or latest session)
    const latestComplaints = sessionsWithData.length > 0 ? formData[sessionsWithData[sessionsWithData.length - 1]]?.[0]?.currentComplaints : '-';
    const complaintsToDisplay = formData?.currentComplaints || latestComplaints || '-';

    const courseSummarySection = {
      title: 'Course Summary',
      fields: [
        { label: 'Total Sessions', value: `${sessionsWithData.length} / ${consultationDates.length}` },
        { label: 'Course Duration', value: consultationDates.length > 0 ? `${moment(consultationDates[0]).format('DD MMM YYYY')} - ${moment(consultationDates[consultationDates.length - 1]).format('DD MMM YYYY')}` : '-' },
        { label: 'Exercises Completed', value: `${totalExercisesCompleted} / ${totalExercises}` },
        { label: 'Current Complaints', value: complaintsToDisplay },
      ]
    };

    // 3. Consolidate all history into one compact table
    const historyTable = {
      title: 'Full Yoga Course History',
      type: 'table',
      headers: ['Sr.', 'Date', 'Exercises (Completed)', 'Notes'],
      data: consultationDates.map((date, index) => {
        const dayData = formData?.[date]?.[0];
        if (!dayData) return null;

        const completedCount = dayData.exercise?.filter(e => e.completed === true || e.completed === 'true').length || 0;
        const totalCount = dayData.exercise?.length || 0;
        const exerciseList = dayData.exercise?.map(ex =>
          `${ex.title}${(ex.completed === true || ex.completed === 'true') ? ' (✓)' : ''}`
        ).join(', ') || '-';

        return [
          index + 1,
          moment(date).format('DD-MM-YYYY'),
          `${exerciseList} [${completedCount}/${totalCount}]`,
          dayData.notes || '-'
        ];
      }).filter(Boolean)
    };

    if (historyTable.data.length === 0) {
      message.warning('No session data available to print');
      return;
    }

    openPrintPreview(displayData, 'Full Yoga Course Report', {
      sections: [patientInfoSection, courseSummarySection, historyTable],
      title: `Yoga Consultation History: ${displayData?.name || 'Patient'}`
    });
  };
  // Helper function to generate all dates between start and end date
  const generateDateRange = (startDate, endDate) => {
    const dates = [];
    const start = moment(startDate, ['DD-MM-YYYY', 'YYYY-MM-DD']);
    const end = moment(endDate, ['DD-MM-YYYY', 'YYYY-MM-DD']);

    while (start.isSameOrBefore(end)) {
      dates.push(start.format('YYYY-MM-DD'));
      start.add(1, 'day');
    }

    return dates;
  };

  // Fetch treatment slot data using useFetchOnce hook
  const { loading: treatmentLoading } = useFetchOnce({
    action: slotAction,
    actionParams: { Query: `/${slotId}` },
    dependency: slotId,
    enabled: !!slotId,
    onSuccess: (response) => {
      if (response?.data?.[0]?.yogaConsultation) {
        setFormData(response.data[0].yogaConsultation);
        if (response?.data?.[0]?.startDate && response?.data?.[0]?.endDate) {
          const dates = generateDateRange(response?.data?.[0].startDate, response?.data?.[0].endDate);
          setConsultationDates(dates);
        }
      }
    },
  });

  // Fetch exercise list using useFetchOnce hook
  const { loading: exercisesLoading } = useFetchOnce({
    action: ActionGetExerciseList,
    actionParams: { Query: '?limit=100' },
    dependency: 'all',
  });


  return (
    <>
      <Header />
      <Sidebar />
      <div className="page-wrapper">
        <div className="content">
          <Breadcrumb page={BreadcrumbList} />

          {/* Patient Information Section */}
          <div className="mb-4">
            <PatientCCPaperView userId={id} extraInfo={{ "Room Name": roomName, "Room Type": roomType }} />
          </div>

          {/* Main Content Split View */}
          <div className="yoga-main-container">
            <Formik
              initialValues={(() => {
                const defaultDate = consultationDates.length > 0 ? consultationDates[0] : null;

                // Helper to find common complaints from any session
                const findCommonComplaints = () => {
                  if (!formData) return '';
                  if (formData.currentComplaints) return formData.currentComplaints;
                  const dateWithComplaints = Object.keys(formData).find(
                    date => Array.isArray(formData[date]) && formData[date][0]?.currentComplaints
                  );
                  return dateWithComplaints ? formData[dateWithComplaints][0].currentComplaints : '';
                };

                const commonComplaints = findCommonComplaints();

                if (!defaultDate) return {
                  currentComplaints: commonComplaints,
                  exercise: [],
                  exerciseCompletion: {},
                  notes: '',
                  date: null
                };

                const dayData = formData?.[defaultDate]?.[0];

                if (dayData) {
                  const exComp = dayData.exercise?.reduce((acc, curr) => ({ ...acc, [curr.title]: curr.completed === true || curr.completed === 'true' }), {}) || {};
                  return {
                    currentComplaints: commonComplaints || dayData.currentComplaints || '',
                    exercise: dayData.exercise?.map(ex => ex.title) || [],
                    exerciseCompletion: exComp,
                    notes: dayData.notes || '',
                    date: defaultDate,
                  };
                }

                // Fallback: Copy from latest if available
                if (formData && Object.keys(formData).length > 0) {
                  const sessionDates = Object.keys(formData).filter(d => Array.isArray(formData[d])).sort();
                  const latestDate = sessionDates[sessionDates.length - 1];
                  const latestEntry = formData[latestDate]?.[0];
                  if (latestEntry) {
                    return {
                      currentComplaints: commonComplaints,
                      exercise: latestEntry.exercise?.map(ex => ex.title) || [],
                      exerciseCompletion: {},
                      notes: '',
                      date: defaultDate
                    };
                  }
                }

                return {
                  currentComplaints: commonComplaints,
                  exercise: [],
                  exerciseCompletion: {},
                  notes: '',
                  date: defaultDate,
                };
              })()}
              enableReinitialize={true}
              validationSchema={yogaFormValidationSchema}
              onSubmit={(values, { setErrors, setSubmitting }) => {
                if (!slotId) { setSubmitting(false); return; }

                // Start with existing data to ensure we don't overwrite history
                let yogaForm = { ...formData };

                const exercisesWithId = safeArray(exercises?.docs)
                  .filter(item => Array.isArray(values?.exercise) && values.exercise.includes(item.title))
                  .map(({ _id, title }) => ({
                    id: _id,
                    title,
                    completed: values.exerciseCompletion?.[title] || false
                  }));

                const DateStr = moment(values.date).format('YYYY-MM-DD');
                if (DateStr) {
                  let entry = {
                    currentComplaints: values.currentComplaints,
                    exercise: exercisesWithId,
                    notes: values.notes,
                  };
                  yogaForm[DateStr] = [entry];

                  // Sync currentComplaints across all sessions to keep it "common"
                  Object.keys(yogaForm).forEach(dateKey => {
                    if (Array.isArray(yogaForm[dateKey]) && yogaForm[dateKey][0]) {
                      yogaForm[dateKey][0].currentComplaints = values.currentComplaints;
                    }
                  });

                  // Ensure we don't send the field at root if it was previously set
                  delete yogaForm.currentComplaints;
                }

                dispatch(slotUpdateAction({
                  Query: slotId,
                  obj: { yogaConsultation: yogaForm },
                },
                  Response => {
                    if (Response?.statusCode === 200) {
                      message.success('Session Saved Successfully');
                      window.history.back();
                    } else if (Response?.statusCode === 400) {
                      setErrors(Response?.data?.errors);
                    }
                    setSubmitting(false);
                  }
                )
                );
              }}
            >
              {({ errors, values, handleChange, handleSubmit, isSubmitting, touched, setFieldValue, dirty }) => {
                const handleDateSelect = (date) => {
                  const dayData = formData?.[date]?.[0];
                  setFieldValue('date', date);

                  if (dayData) {
                    const exList = dayData.exercise?.map(e => e.title) || [];
                    const exComp = dayData.exercise?.reduce((acc, curr) => ({ ...acc, [curr.title]: curr.completed === true || curr.completed === 'true' }), {}) || {};
                    setFieldValue('exercise', exList);
                    setFieldValue('exerciseCompletion', exComp);
                    setFieldValue('notes', dayData.notes || '');
                  } else {
                    // Smart Pre-fill: If the new date is empty, we keep the *current form state fields* (which likely has data from previous view)
                    // But we clear the IDs/Status to treat it as a "New Entry based on Template"
                    setFieldValue('exerciseCompletion', {});
                    setFieldValue('notes', '');
                  }
                };

                return (
                  <Row gutter={24}>
                    {/* Left Column: Session List */}
                    <Col xs={24} lg={9}>
                      <div className="yoga-card session-list-card" style={{ height: 'calc(100vh - 140px)', background: 'white', borderRadius: '12px', padding: '16px', overflowY: 'auto' }}>
                        <div className="card-header-simple" style={{ marginBottom: '16px', borderBottom: '1px solid #f0f0f0', paddingBottom: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <h4 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <CalendarOutlined style={{ color: '#1890ff' }} /> Course Schedule
                          </h4>
                          <Tooltip title="Print All Sessions">
                            <Button
                              type="text"
                              icon={<PrinterOutlined />}
                              onClick={handleFullPrint}
                              style={{ color: '#1890ff' }}
                            />
                          </Tooltip>
                        </div>
                        <div className="session-table-wrapper">
                          <Table
                            dataSource={consultationDates.map((date, idx) => {
                              const isCurrentDate = date === values.date;
                              let completed = 0;
                              let total = 0;
                              let hasData = false;

                              if (isCurrentDate) {
                                // Real-time mapping from formik values
                                total = values.exercise?.length || 0;
                                completed = values.exercise?.filter(exTitle => values.exerciseCompletion?.[exTitle]).length || 0;
                                hasData = total > 0 || !!values.notes;
                              } else {
                                // Mapping from saved formData
                                const dayData = formData?.[date]?.[0];
                                const dayExercises = dayData?.exercise || [];
                                completed = dayExercises.filter(e => e.completed === true || e.completed === "true").length;
                                total = dayExercises.length;
                                hasData = !!dayData;
                              }

                              const percent = total > 0 ? Math.round((completed / total) * 100) : 0;

                              return {
                                key: date,
                                date,
                                stats: { total, completed, percent },
                                hasData
                              };
                            })}
                            columns={[
                              {
                                title: 'Date',
                                dataIndex: 'date',
                                key: 'date',
                                render: (date, record) => (
                                  <div className="date-cell">
                                    <div className="date-text" style={{ fontWeight: 600 }}>{moment(date).format('DD MMM')}</div>
                                    <div className="day-text" style={{ fontSize: '12px', color: '#8c8c8c' }}>{moment(date).format('dddd, YYYY')}</div>
                                  </div>
                                )
                              },
                              {
                                title: 'Status',
                                key: 'status',
                                align: 'right',
                                render: (_, record) => {
                                  if (!record.hasData) return <Tag>Pending</Tag>;
                                  return (
                                    <div className="status-cell" style={{ width: '100px', textAlign: 'right' }}>
                                      <div style={{ fontSize: '11px', color: '#8c8c8c' }}>{record.stats.completed}/{record.stats.total} Done</div>
                                      <Progress percent={record.stats.percent} size="small" showInfo={false} strokeColor="#52c41a" />
                                    </div>
                                  );
                                }
                              }
                            ]}
                            pagination={false}
                            size="small"
                            showHeader={false}
                            rowClassName={(record) => values.date === record.date ? 'ant-table-row-selected' : ''}
                            onRow={(record) => ({
                              onClick: () => handleDateSelect(record.date),
                              style: { cursor: 'pointer', background: values.date === record.date ? '#e6f7ff' : 'transparent' }
                            })}
                          />
                        </div>
                      </div>
                    </Col>

                    {/* Right Column: Active Session Editor */}
                    <Col xs={24} lg={15}>
                      <div className="yoga-card editor-card" style={{ background: 'white', borderRadius: '12px', padding: '24px', position: 'relative', height: 'calc(100vh - 140px)', overflowY: 'auto' }}>
                        <div className="editor-header" style={{ marginBottom: '24px' }}>
                          <div className="d-flex justify-content-between align-items-center">
                            <div>
                              <div style={{ textTransform: 'uppercase', fontSize: '11px', fontWeight: 700, color: '#1890ff', letterSpacing: '1px' }}>
                                ACTIVE SESSION
                              </div>
                              <h3 className="mb-0" style={{ fontWeight: 700 }}>
                                {moment(values.date).format('DD MMMM YYYY')}
                              </h3>
                              <span style={{ color: '#8c8c8c' }}>{moment(values.date).format('dddd')}</span>
                            </div>
                            <div className="d-flex gap-2">
                              <Tooltip title="Print Today's Session">
                                <Button
                                  icon={<PrinterOutlined />}
                                  onClick={() => handleSinglePrint(values)}
                                >
                                  Print
                                </Button>
                              </Tooltip>
                              <Tag color="blue">{values.exercise?.length || 0} Exercises</Tag>
                            </div>
                          </div>
                        </div>

                        <div className="editor-body">
                          {/* Exercise Manager */}
                          <div className="form-section mb-4">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <label className="form-label-custom mb-0" style={{ fontWeight: 600 }}>Daily Routine</label>
                            </div>

                            <div className="add-exercise-row mb-3">
                              <InputFromUser
                                placeholder="Select exercises to add..."
                                type="multiple"
                                name="exercise"
                                options={safeArray(exercises?.docs)?.map(v => v.title) || []}
                                value={values.exercise}
                                onChange={v => {
                                  setFieldValue('exercise', v);
                                  // Init completion status for new exercises
                                  const newCompletion = { ...values.exerciseCompletion };
                                  v.forEach(ex => {
                                    if (newCompletion[ex] === undefined) newCompletion[ex] = false;
                                  });
                                  setFieldValue('exerciseCompletion', newCompletion);
                                }}
                                error={errors.exercise}
                                touched={touched.exercise}
                                FormGroupClassName="mb-0 flex-grow-1"
                              />
                            </div>

                            <div className="daily-routine-list" style={{ background: '#fafafa', borderRadius: '8px', padding: '12px', border: '1px solid #f0f0f0', minHeight: '100px' }}>
                              {values.exercise?.length === 0 ? (
                                <div className="text-center py-4" style={{ color: '#bfbfbf' }}>
                                  <p className="mb-0">No exercises scheduled for this day.</p>
                                </div>
                              ) : (
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '8px' }}>
                                  {values.exercise?.map((exTitle, idx) => {
                                    const isDone = values.exerciseCompletion?.[exTitle];
                                    return (
                                      <div key={idx}
                                        className={`d-flex align-items-center justify-content-between p-2 rounded ${isDone ? 'bg-success-light' : 'bg-white'}`}
                                        style={{ border: isDone ? '1px solid #b7eb8f' : '1px solid #e8e8e8', background: isDone ? '#f6ffed' : 'white', transition: 'all 0.2s', cursor: 'pointer' }}
                                        onClick={() => setFieldValue(`exerciseCompletion.${exTitle}`, !isDone)}
                                      >
                                        <div className="d-flex align-items-center gap-2" style={{ flex: 1 }}>
                                          {isDone ?
                                            <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} /> :
                                            <div style={{ width: '18px', height: '18px', borderRadius: '50%', border: '2px solid #d9d9d9' }} />
                                          }
                                          <span style={{ fontWeight: 500, color: isDone ? '#52c41a' : '#262626', textDecoration: isDone ? 'line-through' : 'none' }}>
                                            {exTitle}
                                          </span>
                                        </div>
                                        <Button
                                          type="text"
                                          danger
                                          size="small"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const newEx = values.exercise.filter(e => e !== exTitle);
                                            setFieldValue('exercise', newEx);
                                          }}
                                          icon={<span style={{ fontSize: '18px', lineHeight: 1 }}>×</span>}
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Observations */}
                          <div className="form-section mb-4">
                            <label className="form-label-custom" style={{ fontWeight: 600 }}>Observations & Complaints</label>
                            <textarea
                              className="custom-textarea"
                              rows={10}
                              placeholder="Patient's feedback, pain points, or specific observations for today..."
                              value={values.currentComplaints}
                              onChange={e => setFieldValue('currentComplaints', e.target.value)}
                              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${errors.currentComplaints && touched.currentComplaints ? '#ff4d4f' : '#d9d9d9'}` }}
                            />
                            {errors.currentComplaints && touched.currentComplaints && (
                              <div className="text-danger mt-1">{errors.currentComplaints}</div>
                            )}
                          </div>

                          {/* Notes */}
                          {/* <div className="form-section mb-4">
                            <label className="form-label-custom" style={{ fontWeight: 600 }}>Session Notes</label>
                            <textarea
                              className="custom-textarea"
                              rows={3}
                              placeholder="Additional internal notes for this session..."
                              value={values.notes}
                              onChange={e => setFieldValue('notes', e.target.value)}
                              style={{ width: '100%', padding: '12px', borderRadius: '8px', border: `1px solid ${errors.notes && touched.notes ? '#ff4d4f' : '#d9d9d9'}` }}
                            />
                            {errors.notes && touched.notes && (
                              <div className="text-danger mt-1">{errors.notes}</div>
                            )}
                          </div> */}
                        </div>

                        <div className="editor-footer" style={{ borderTop: '1px solid #f0f0f0', paddingTop: '16px', display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                          <Button onClick={() => window.history.back()}>Back</Button>
                          <Button
                            type="primary"
                            onClick={handleSubmit}
                            loading={isSubmitting}
                            icon={<SendOutlined />}
                            size="large"
                            style={{ borderRadius: '8px', paddingLeft: '32px', paddingRight: '32px' }}
                          >
                            Save Session
                          </Button>
                        </div>
                      </div>
                    </Col>
                  </Row>
                );
              }}
            </Formik>
          </div>
        </div>
      </div>
    </>
  );
};

export default YogaConsultation;
